import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaLock, FaGraduationCap, FaSchool, FaIdCard, FaGlobe } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmNew: '' });
  const [age, setAge] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [profileMsg, setProfileMsg] = useState(null);

  const fetchProfile = async () => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem('user'));
      if (!loggedInUser || !loggedInUser.id) { navigate('/login'); return; }
      const response = await fetch(`${API_BASE_URL}/get_profile.php?id=${loggedInUser.id}`);
      const result = await response.json();
      if (result.status === 'success') {
        setUserData(result.data);
        if (result.data.birthday) {
          const birthDate = new Date(result.data.birthday);
          const today = new Date();
          let calculatedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) calculatedAge--;
          setAge(calculatedAge);
        }
      } else { setError(result.message); }
    } catch (err) { setError("Failed to connect to server."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    formData.append('userId', userData.id);
    try {
      const response = await fetch(`${API_BASE_URL}/upload_profile_image.php`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.status === 'success') {
        setUserData({ ...userData, profileImage: data.image_url });
        setProfileMsg({ type: 'success', text: "Profile image updated!" });
      } else { setProfileMsg({ type: 'error', text: data.message }); }
    } catch (err) { setProfileMsg({ type: 'error', text: "Failed to upload image." }); }
    finally { setImageUploading(false); }
  };

  const handlePasswordChange = (e) => setPasswords({ ...passwords, [e.target.name]: e.target.value });
  const handleProfileChange = (e) => setUserData({ ...userData, [e.target.name]: e.target.value });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_BASE_URL}/update_profile.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userData.id, fullName: userData.fullName, phone: userData.phone,
          address: userData.address, birthday: userData.birthday, instituteName: userData.instituteName,
          schoolName: userData.schoolName, grade: userData.grade, medium: userData.medium, district: userData.district
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setProfileMsg({ type: 'success', text: "Profile updated successfully!" });
        setEditMode(false);
        fetchProfile();
      } else { setProfileMsg({ type: 'error', text: data.message }); }
    } catch (err) { setProfileMsg({ type: 'error', text: "Failed to update profile." }); }
    finally { setSavingProfile(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (passwords.newPassword !== passwords.confirmNew) { setMsg({ type: 'error', text: "New passwords do not match." }); return; }
    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/update_password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userData.id, currentPassword: passwords.currentPassword, newPassword: passwords.newPassword })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setMsg({ type: 'success', text: "Password updated successfully!" });
        setPasswords({ currentPassword: '', newPassword: '', confirmNew: '' });
      } else { setMsg({ type: 'error', text: data.message }); }
    } catch (err) { setMsg({ type: 'error', text: "Failed to update password." }); }
    finally { setUpdating(false); }
  };

  if (loading) return (
    <DashboardLayout title="Loading...">
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout title="Error">
      <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-center font-bold text-sm">{error}</div>
    </DashboardLayout>
  );

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${userData.indexNumber}`;

  return (
    <DashboardLayout title="Student Profile">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-4 sm:gap-6 pb-8">

        {/* Left: ID Card */}
        <aside className="w-full lg:w-72 flex-shrink-0 space-y-3 sm:space-y-4">

          {/* Mobile: Horizontal profile strip */}
          <div className="bg-[#001f3f] rounded-xl p-4 sm:p-6 text-white shadow-lg overflow-hidden">

            {/* Mobile layout: photo left, name/index right */}
            <div className="flex items-center gap-4 lg:flex-col lg:items-center lg:text-center lg:gap-0">

              {/* Profile Photo */}
              <div className="relative flex-shrink-0 lg:mb-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-28 lg:h-28 rounded-xl bg-slate-800 flex items-center justify-center overflow-hidden border-2 border-white/10 shadow-xl relative">
                  {imageUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-20">
                      <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  {userData.profileImage ? (
                    <img
                      src={userData.profileImage.startsWith('http') ? userData.profileImage : `${API_BASE_URL.replace(/\/api$/, '')}/${userData.profileImage}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaUser size={28} className="text-white/20" />
                  )}
                </div>
                <label className="absolute -bottom-1.5 -right-1.5 w-7 h-7 lg:w-8 lg:h-8 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center justify-center cursor-pointer shadow-lg transition-all active:scale-90 z-30">
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={imageUploading} />
                  <FaIdCard size={11} className="text-white" />
                </label>
              </div>

              {/* Name + Index — side by side on mobile, centered on desktop */}
              <div className="flex-grow lg:w-full lg:text-center lg:mb-4">
                {editMode ? (
                  <input
                    name="fullName"
                    value={userData.fullName}
                    onChange={handleProfileChange}
                    className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-sm font-bold text-center outline-none focus:border-orange-500"
                  />
                ) : (
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold tracking-tight leading-tight">{userData.fullName}</h3>
                )}
                <p className="text-[9px] sm:text-[10px] font-bold text-orange-400 uppercase tracking-widest mt-0.5">{userData.indexNumber}</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-[8px] font-black uppercase tracking-widest">Active</span>
              </div>
            </div>

            {/* QR Code — compact */}
            <div className="mt-4 w-full bg-white rounded-xl p-3 shadow-inner flex flex-col items-center">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest text-center mb-2">Student QR Code</p>
              <img
                src={qrCodeUrl}
                alt="QR Code"
                className="mix-blend-multiply"
                style={{ width: '120px', height: '120px' }}
              />
              <p className="text-[8px] font-bold text-slate-400 text-center mt-2 uppercase tracking-widest">{userData.indexNumber}</p>
            </div>

            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-[8px] font-bold opacity-50 uppercase tracking-widest">
              <span>Academy 2026</span>
              <span className="text-orange-400">Verified</span>
            </div>
          </div>

          {/* Edit Button */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-100 shadow-sm">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`w-full py-2.5 rounded-lg font-bold text-[10px] uppercase tracking-widest transition-all ${editMode ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-slate-50 text-slate-600 border border-slate-100 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-100'}`}
            >
              {editMode ? '✕ Cancel Editing' : '✎ Edit Profile'}
            </button>
          </div>
        </aside>

        {/* Right: Info Panels */}
        <main className="flex-grow space-y-4 sm:space-y-5">
          <form onSubmit={handleProfileSubmit} className="space-y-4 sm:space-y-5">
            {profileMsg && (
              <div className={`p-3 rounded-xl border font-bold text-xs ${profileMsg.type === 'success' ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600'}`}>
                {profileMsg.text}
              </div>
            )}

            {/* Academic Details */}
            <div className="bg-white rounded-xl border border-slate-100 p-4 sm:p-5 shadow-sm">
              <SectionHeader icon={<FaGraduationCap />} title="Academic Details" color="blue" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <DetailItem label="INSTITUTE" value={userData.instituteName} icon={<FaSchool />} isEditing={editMode} name="instituteName" onChange={handleProfileChange} />
                <DetailItem label="SCHOOL" value={userData.schoolName} icon={<FaGraduationCap />} isEditing={editMode} name="schoolName" onChange={handleProfileChange} />
                <DetailItem label="GRADE" value={userData.grade} icon={<FaUser />} isEditing={editMode} name="grade" onChange={handleProfileChange} type="select"
                  options={['Grade 6','Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12','Grade 13','Revision','Paper Class']} />
                <DetailItem label="MEDIUM" value={userData.medium} icon={<FaGlobe />} isEditing={editMode} name="medium" onChange={handleProfileChange} type="select"
                  options={['Sinhala','English']} />
              </div>
            </div>

            {/* Personal + Guardian */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <div className="bg-white rounded-xl border border-slate-100 p-4 sm:p-5 shadow-sm">
                <SectionHeader icon={<FaUser />} title="Personal Info" color="orange" />
                <div className="space-y-4 mt-4">
                  <DetailItem label="EMAIL (Read-only)" value={userData.email} icon={<FaEnvelope />} isEditing={false} />
                  <DetailItem label="PHONE" value={userData.phone} icon={<FaPhone />} isEditing={editMode} name="phone" onChange={handleProfileChange} />
                  <DetailItem label="ADDRESS" value={userData.address} icon={<FaMapMarkerAlt />} isEditing={editMode} name="address" onChange={handleProfileChange} />
                  <DetailItem label="BIRTHDAY" value={userData.birthday} icon={<FaIdCard />} isEditing={editMode} name="birthday" type="date" onChange={handleProfileChange} />
                  <DetailItem label="DISTRICT" value={userData.district} icon={<FaGlobe />} isEditing={editMode} name="district" onChange={handleProfileChange} type="select"
                    options={['Ampara','Anuradhapura','Badulla','Batticaloa','Colombo','Galle','Gampaha','Hambantota','Jaffna','Kalutara','Kandy','Kegalle','Kilinochchi','Kurunegala','Mannar','Matale','Matara','Moneragala','Mullaitivu','Nuwara Eliya','Polonnaruwa','Puttalam','Ratnapura','Trincomalee','Vavuniya']} />
                </div>
              </div>

              <div className="space-y-4 sm:space-y-5">
                <div className="bg-white rounded-xl border border-slate-100 p-4 sm:p-5 shadow-sm">
                  <SectionHeader icon={<FaUser />} title="Guardian Details" color="green" />
                  <div className="space-y-4 mt-4">
                    <DetailItem label="NAME (Read-only)" value={userData.guardianName} icon={<FaUser />} isEditing={false} />
                    <DetailItem label="PHONE (Read-only)" value={userData.guardianPhone} icon={<FaPhone />} isEditing={false} />
                  </div>
                </div>
                {editMode && (
                  <button type="submit" disabled={savingProfile}
                    className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] uppercase tracking-widest rounded-xl shadow transition-all active:scale-95 disabled:opacity-50">
                    {savingProfile ? 'Saving Changes...' : 'Save Profile Changes'}
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* Security Form */}
          <form onSubmit={handlePasswordSubmit} className="bg-slate-900 rounded-xl p-4 sm:p-5 text-white shadow-lg space-y-3">
            <div className="flex items-center gap-2">
              <FaLock className="text-red-400" size={12} />
              <h3 className="font-bold text-xs sm:text-sm">Security Settings</h3>
            </div>
            {msg && (
              <div className={`text-[10px] font-bold p-2 rounded-lg ${msg.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {msg.text}
              </div>
            )}
            <div className="space-y-2.5">
              <SecurityInput placeholder="Current Password" name="currentPassword" value={passwords.currentPassword} onChange={handlePasswordChange} />
              <SecurityInput placeholder="New Password" name="newPassword" value={passwords.newPassword} onChange={handlePasswordChange} />
              <SecurityInput placeholder="Confirm New" name="confirmNew" value={passwords.confirmNew} onChange={handlePasswordChange} />
              <button type="submit" disabled={updating}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all disabled:opacity-50">
                {updating ? 'Updating...' : 'Update Security'}
              </button>
            </div>
          </form>
        </main>
      </div>
    </DashboardLayout>
  );
};

const SectionHeader = ({ icon, title, color }) => {
  const colors = {
    orange: "bg-orange-50 text-orange-500",
    blue: "bg-blue-50 text-blue-500",
    green: "bg-green-50 text-green-600",
    red: "bg-red-50 text-red-500"
  };
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-8 h-8 sm:w-9 sm:h-9 ${colors[color]} rounded-lg flex items-center justify-center text-sm`}>{icon}</div>
      <h3 className="text-xs sm:text-sm font-bold text-slate-700">{title}</h3>
    </div>
  );
};

const DetailItem = ({ label, value, icon, isEditing, name, onChange, type = "text", options = [] }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-300 flex-shrink-0 text-xs">{icon}</div>
    <div className="flex-grow min-w-0">
      <p className="text-[8px] sm:text-[9px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
      {isEditing ? (
        type === "select" ? (
          <select name={name} value={value} onChange={onChange}
            className="w-full bg-slate-50 border border-slate-100 rounded px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:border-orange-500 mt-0.5">
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        ) : (
          <input type={type} name={name} value={value} onChange={onChange}
            className="w-full bg-slate-50 border border-slate-100 rounded px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:border-orange-500 mt-0.5" />
        )
      ) : (
        <p className="text-xs sm:text-sm font-bold text-slate-700 truncate">{value || '—'}</p>
      )}
    </div>
  </div>
);

const SecurityInput = ({ placeholder, name, value, onChange }) => (
  <input type="password" name={name} value={value} onChange={onChange} required
    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg outline-none focus:border-orange-500/50 transition-all font-bold text-xs text-white placeholder-white/20"
    placeholder={placeholder} />
);

export default Profile;
